import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '../../../../lib/i18n';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import styles from './TimePicker.module.css';
import { Button, IconButton, Input } from '../../ui';

// The composer's "+ Share a time" picker — a small centered modal with a native
// datetime-local input. On confirm it hands the picked `Date` to the caller, which
// inserts a readable, locale-formatted line into the composer draft. No backend field.
export function TimePicker({ onPick, onClose }: { onPick: (date: Date) => void; onClose: () => void }) {
  const { t } = useTranslation();
  const { logModalActionBi } = useGenericBi('time-picker');
  const [value, setValue] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const confirm = () => {
    if (!value) return;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return;
    logModalActionBi({ page_name: 'chat', modal_name: 'time_picker', action: 'pick_time' });
    onPick(date);
    onClose();
  };

  return createPortal(
    <div
      className={styles.scrim}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div role="dialog" aria-modal="true" aria-label={t('chat.timePicker.title')} className={styles.card}>
        <div className={styles.header}>
          <span className={styles.title}>{t('chat.timePicker.title')}</span>
          <IconButton size="tiny" aria-label={t('common.close')} onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
              <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </IconButton>
        </div>
        <div className={styles.divider} aria-hidden />
        <div className={styles.body}>
          <Input
            type="datetime-local"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            aria-label={t('chat.timePicker.title')}
          />
          <div className={styles.actions}>
            <Button variant="text" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button disabled={!value} onClick={confirm}>
              {t('chat.timePicker.confirm')}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
